import * as R from 'ramda';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
// components
import { FormFooter } from '../../../components/form-footer';
// forms
import { Fieldset2 } from '../../../forms';
import { SelectDropdownForm } from '../../../forms/forms/select-dropdown-form';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
//////////////////////////////////////////////////

const useSharedAccessorialOptions = (primaryObjectBranchGuid) => {
  const [sharedAccessorials, setSharedAccessorials] = useState(null);

  useEffect(() => {
    let mounted = true;

    const getSharedAccessorials = async () => {
      if (R.isNil(primaryObjectBranchGuid)) return;

      const res = await sendRequest('get', endpointsMap.sharedAccessorialGuidNameMap, {
        params: { [GC.FIELD_BRANCH_GUID]: primaryObjectBranchGuid },
      });

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        if (mounted) setSharedAccessorials(data);
      } else {
        G.handleFailResponseSimple(res);
      }
    };

    getSharedAccessorials();

    return () => {
      mounted = false;
    };
  }, [primaryObjectBranchGuid]);

  return G.mapNameGuidObjectPropsToLabelValueObject(R.or(sharedAccessorials, []));
};

const getVendorValidationSchema = (version) => Yup.object().shape({
  [GC.FIELD_FREE_DISTANCE]: G.yupNumberNotRequired,
  [GC.FIELD_SHARED_ASSESSORIAL_GUID]: G.ifElse(R.isNil(version), G.yupStringRequired),
});

const vendorInputWrapperStyles = { mb: 25, width: 300 };
const vendorFieldsWrapperStyles = { pt: 15, flexDirection: 'column' };

const vendorFields = [
  {
    isRequired: true,
    type: 'reactSelect',
    options: 'sharedAccessorialOptions',
    fieldName: GC.FIELD_SHARED_ASSESSORIAL_GUID,
    disabled: (_, { version }) => R.isNotNil(version),
    label: ['titles:shared-accessorial', 'Shared Accessorial'],
    inputWrapperStyles: {
      ...vendorInputWrapperStyles,
      display: (props) => G.ifElse(
        R.isNotNil(R.path(['values', GC.FIELD_VERSION], props)),
        'none',
      ),
    },
  },
  {
    type: 'number',
    fieldName: GC.FIELD_FREE_DISTANCE,
    label: ['titles:free-miles', 'Free Miles'],
    inputWrapperStyles: vendorInputWrapperStyles,
  },
];

const VendorSharedAccessorialFreeMilesForm = (props) => {
  const {
    submitAction,
    primaryObjectGuid,
    initialValues = {},
    sharedAccessorialOptions,
  } = props;

  const onSubmit = (values, { setSubmitting }) => {
    const callback = () => setSubmitting(false);

    submitAction(values, { failCallback: callback, successCallback: callback });
  };

  const formik = useFormik({
    onSubmit,
    validationSchema: getVendorValidationSchema(R.prop(GC.FIELD_VERSION, initialValues)),
    initialValues: G.setInitialFormikValues(
      {
        [GC.FIELD_FREE_DISTANCE]: null,
        [GC.FIELD_SHARED_ASSESSORIAL_GUID]: null,
        [GC.FIELD_FLEET_VENDOR_GUID]: primaryObjectGuid,
      },
      R.pick([GC.FIELD_GUID, GC.FIELD_VERSION, GC.FIELD_FREE_DISTANCE], initialValues),
    ),
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Fieldset2
        {...G.getFormikPropsToFieldset(formik)}
        fields={vendorFields}
        fieldsWrapperStyles={vendorFieldsWrapperStyles}
        sharedAccessorialOptions={sharedAccessorialOptions}
      />
      <FormFooter submitDisabled={formik.isSubmitting} />
    </form>
  );
};

const SharedAccessorialForm = (props) => {
  const {
    fleetType,
    closeModal,
    submitAction,
    initialValues,
    primaryObjectGuid,
    primaryObjectBranchGuid,
  } = props;

  const options = useSharedAccessorialOptions(primaryObjectBranchGuid);

  if (R.equals(fleetType, 'vendor')) {
    return (
      <VendorSharedAccessorialFreeMilesForm
        cancelAction={closeModal}
        submitAction={submitAction}
        initialValues={initialValues}
        sharedAccessorialOptions={options}
        primaryObjectGuid={primaryObjectGuid}
      />
    );
  }

  const handleSubmit = (selectedGuids) => submitAction({
    primaryObjectGuid,
    [GC.FIELD_ACCESSORIAL_SHARED_ACCESSORIAL_GUIDS]: selectedGuids,
  });

  return (
    <SelectDropdownForm
      isMulti={true}
      options={options}
      cancelAction={closeModal}
      submitAction={handleSubmit}
      fieldLabel={['titles:shared-accessorials']}
    />
  );
};

export default SharedAccessorialForm;
